import { Link } from "react-router-dom";
import { useState } from "react";
import { Shield, ArrowRight, Lock, Anchor } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";
import { api } from "@/lib/api";

export default function CyberOpsHero() {
    const { tenant } = useTenant();
    const [form, setForm] = useState({ name: "", email: "", company: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const headline = tenant?.hero_headline || "Sovereign Defence.";
    const kicker = tenant?.hero_kicker || "CyberOps Watch";
    const subhead =
        tenant?.subhead || "Territorial cyber posture for high-trust operators.";
    const stateName = tenant?.state_name || (tenant?.state || "").toUpperCase();
    const wayfinder = tenant?.wayfinder_name;
    const accent = tenant?.accent || "#D4A04B";
    const baseColor = tenant?.color || "#1A432B";

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.includes("@")) {
            toast.error("Name + valid email required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/hub/leads", {
                ...form,
                source_path: "/cyberops-hero",
            });
            toast.success(
                `Intel logged. ${wayfinder || "A Wayfinder"} will signal you.`,
            );
            setSubmitted(true);
        } catch {
            toast.error("Message not sent — try again");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className="relative text-cream overflow-hidden"
            style={{ background: baseColor }}
            data-testid="cyberops-hero"
            data-state={tenant?.state}
        >
            <div className="grain absolute inset-0 opacity-50" />
            <div
                className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full blur-3xl opacity-20"
                style={{ background: accent }}
                aria-hidden
            />
            <div
                className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-15"
                style={{ background: accent }}
                aria-hidden
            />

            <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 pb-20 md:pt-28 md:pb-32 grid md:grid-cols-12 gap-12 items-center">
                <div className="md:col-span-7 rise-in">
                    <span
                        className="divider-rule"
                        style={{ color: accent }}
                    >
                        <Shield size={12} /> {kicker} · {stateName}
                    </span>
                    <h1 className="font-serif text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] leading-[0.94] mt-8">
                        <span className="block text-cream">
                            {headline.split(" ")[0] || headline}
                        </span>
                        {headline.split(" ").length > 1 && (
                            <span
                                className="block italic"
                                style={{ color: accent }}
                            >
                                {headline.split(" ").slice(1).join(" ")}
                            </span>
                        )}
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-cream/75 leading-relaxed max-w-xl">
                        {subhead}
                    </p>
                    {wayfinder && (
                        <p
                            className="mt-4 text-[0.7rem] tracking-[0.3em] uppercase"
                            style={{ color: accent }}
                            data-testid="cyberops-wayfinder"
                        >
                            <Anchor size={11} className="inline mr-2" />
                            Your Wayfinder · {wayfinder} · {tenant?.wayfinder_id}
                        </p>
                    )}
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <Link
                            to="/star-map"
                            className="btn-pill"
                            style={{
                                background: accent,
                                color: baseColor,
                                border: `1px solid ${accent}`,
                            }}
                            data-testid="cyberops-starmap-btn"
                        >
                            <Lock size={14} /> Run Star-Map Scan{" "}
                            <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/mission"
                            className="btn-pill border border-cream/40 text-cream hover:bg-cream hover:text-[color:var(--ig-forest)]"
                        >
                            EIGOO Manifest
                        </Link>
                    </div>
                </div>

                <div className="md:col-span-5">
                    {submitted ? (
                        <div
                            className="p-8 border rounded-sm bg-black/15 backdrop-blur-sm"
                            style={{ borderColor: `${accent}66` }}
                            data-testid="cyberops-lead-success"
                        >
                            <Shield size={32} style={{ color: accent }} />
                            <h3 className="font-serif text-2xl mt-4 text-cream">
                                Channel opened.
                            </h3>
                            <p className="mt-2 text-cream/70 text-sm leading-relaxed">
                                {wayfinder} has been pinged. Check your inbox
                                — you'll receive secure handoff within 24h.
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            className="p-7 border rounded-sm bg-black/15 backdrop-blur-sm space-y-3"
                            style={{ borderColor: `${accent}55` }}
                            data-testid="cyberops-lead-form"
                        >
                            <p
                                className="text-[0.65rem] tracking-[0.3em] uppercase"
                                style={{ color: accent }}
                            >
                                Open Channel · Wayfinder Ping
                            </p>
                            <input
                                type="text"
                                placeholder="Operator name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm((s) => ({ ...s, name: e.target.value }))
                                }
                                className="w-full bg-transparent border-b border-cream/30 focus:border-[color:inherit] py-2 outline-none text-cream placeholder:text-cream/40"
                                style={{ "--tw-border-opacity": 1 }}
                                data-testid="cyberops-lead-name"
                            />
                            <input
                                type="email"
                                placeholder="secure@email.com"
                                value={form.email}
                                onChange={(e) =>
                                    setForm((s) => ({ ...s, email: e.target.value }))
                                }
                                className="w-full bg-transparent border-b border-cream/30 py-2 outline-none text-cream placeholder:text-cream/40"
                                data-testid="cyberops-lead-email"
                            />
                            <input
                                type="text"
                                placeholder="Company / Platform"
                                value={form.company}
                                onChange={(e) =>
                                    setForm((s) => ({
                                        ...s,
                                        company: e.target.value,
                                    }))
                                }
                                className="w-full bg-transparent border-b border-cream/30 py-2 outline-none text-cream placeholder:text-cream/40"
                                data-testid="cyberops-lead-company"
                            />
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-pill w-full justify-center disabled:opacity-50"
                                style={{
                                    background: accent,
                                    color: baseColor,
                                    border: `1px solid ${accent}`,
                                }}
                                data-testid="cyberops-lead-submit"
                            >
                                {submitting ? "Pinging…" : "Ping My Wayfinder"}
                            </button>
                            <p
                                className="text-[0.6rem] tracking-[0.3em] uppercase text-center text-cream/50"
                            >
                                Auto-tagged · {stateName} · {tenant?.wayfinder_id}
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
